import { formatPlate, normalizePhone } from "@station/core";

/**
 * KVKK — kişisel alanların EKRANDA gösterimi (Prompt 17 web). Saf fonksiyonlar (birim testli).
 *
 * Kural: maskelemeyi SUNUCU yapar (`customers.personal.view` yoksa ad `A*** Y***`, telefon `+90532*****67`, e-posta
 * `a***@ornek.com`, plaka `34****23` döner ve yanıtta `personalDataMasked: true` yazar). İstemci maskeli değeri ASLA
 * "açmaya" çalışmaz, yeniden biçimlendirmez (biçimlendirme maskeyi bozabilir: `34****23` bir plaka değildir) ve
 * düzenleme formuna geri yazmaz — maskeli değer yalnızca olduğu gibi gösterilir ve "maskeli" diye işaretlenir.
 */

/** Sunucunun maske karakteri. Maskeli alanlarda en az bir `*` bulunur; açık veride `*` beklenmez. */
export function isMaskedText(value: string | null | undefined): boolean {
  return typeof value === "string" && value.includes("*");
}

export interface PersonalDisplay {
  /** Ekranda yazılacak metin; boş alan için `null` (çağıran "—" çizer). */
  text: string | null;
  /** Değer maskeli mi (yanıt bayrağı ya da değerin kendisi). */
  masked: boolean;
}

/**
 * Kişisel alanın gösterimi. `responseMasked` = yanıttaki `personalDataMasked`. Yanıt maskeli dediyse boş olmayan her
 * alan maskeli sayılır (not alanı `***` gibi `*` içermeyen biçimlerde de gelebilir).
 */
export function personalDisplay(
  value: string | null | undefined,
  responseMasked: boolean,
  format?: (value: string) => string,
): PersonalDisplay {
  if (value === null || value === undefined || value.trim() === "") return { text: null, masked: false };
  const masked = responseMasked || isMaskedText(value);
  return { text: masked || !format ? value : format(value), masked };
}

/** E.164 Türkiye numarası okunur biçimde: `+905321234567` → `+90 532 123 45 67`. Diğer ülkeler olduğu gibi kalır. */
export function formatPhoneDisplay(phone: string): string {
  if (isMaskedText(phone)) return phone;
  const match = /^\+90(\d{3})(\d{3})(\d{2})(\d{2})$/.exec(phone);
  return match ? `+90 ${match[1]} ${match[2]} ${match[3]} ${match[4]}` : phone;
}

/** Normalize plaka okunur biçimde (`34ABC123` → `34 ABC 123`); maskeli plaka DOKUNULMADAN döner. */
export function formatPlateDisplay(plate: string): string {
  return isMaskedText(plate) ? plate : formatPlate(plate);
}

/**
 * Düzenleme formunun başlangıç değeri: maskeli alan forma YAZILMAZ (kullanıcı `A*** Y***`'yı kaydedip gerçek adı
 * bozmasın). Kişisel veriyi göremeyen kullanıcı zaten o alanı düzenleyemez; form alanı boş ve kilitli gelir.
 */
export function editableValue(value: string | null | undefined, responseMasked: boolean): string {
  if (value === null || value === undefined) return "";
  return responseMasked || isMaskedText(value) ? "" : value;
}

/**
 * Formdaki telefon metni → API değeri. Boş → `null`; Türkiye yazımları E.164'e çevrilir; çevrilemeyen metin olduğu gibi
 * gider (sunucu doğrular ve alan hatası döner — istemci sessizce düzeltmez).
 */
export function phoneInputToApi(text: string): string | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  return normalizePhone(trimmed) ?? trimmed;
}
